/**
 * Audit Timeline Component
 * 
 * Displays a chronological timeline of audit entries
 */

import type { AuditEntry } from '@/types/audit';
import { AuditEntryCard } from './AuditEntryCard';
import { ScrollArea, ScrollAreaScrollbar, ScrollAreaViewport } from '@/components/ui/scroll-area';
import { Clock } from 'lucide-react';

interface AuditTimelineProps {
  entries: AuditEntry[];
}

interface CycleGroup {
  cycleNumber: number;
  label: string;
  entries: AuditEntry[];
}

/** Groups entries by submission cycle, newest cycle first; null when there is a single cycle. */
function groupEntriesByCycle(entries: AuditEntry[]): CycleGroup[] | null {
  const groups = new Map<number, CycleGroup>();
  for (const entry of entries) {
    if (entry.cycleNumber == null) continue;
    const group = groups.get(entry.cycleNumber)
      ?? { cycleNumber: entry.cycleNumber, label: entry.cycleLabel ?? `Submission ${entry.cycleNumber}`, entries: [] };
    group.entries.push(entry);
    groups.set(entry.cycleNumber, group);
  }

  if (groups.size < 2) return null;
  return [...groups.values()].sort((a, b) => b.cycleNumber - a.cycleNumber);
}

export function AuditTimeline({ entries }: AuditTimelineProps) {
  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <div className="text-4xl mb-2">📋</div>
        <p>No audit history available</p>
      </div>
    );
  }

  const cycleGroups = groupEntriesByCycle(entries);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-lg bg-blue-50">
          <Clock className="h-5 w-5 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold">Timeline</h3>
        <span className="text-sm text-muted-foreground">
          ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})
        </span>
      </div>
      
      <ScrollArea className="h-[400px]">
        <ScrollAreaViewport className="h-full pr-4">
          {cycleGroups ? (
            <div className="space-y-4">
              {cycleGroups.map((group) => (
                <section key={group.cycleNumber} aria-label={group.label}>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-2">{group.label}</h4>
                  <div className="space-y-0">
                    {group.entries.map((entry) => (
                      <AuditEntryCard key={entry.id} entry={entry} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="space-y-0">
              {entries.map((entry) => (
                <AuditEntryCard key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </ScrollAreaViewport>
        <ScrollAreaScrollbar orientation="vertical" />
      </ScrollArea>
    </div>
  );
}
